import React, { useState, useCallback, useMemo } from 'react';
import { 
  BarChart3, 
  TrendingUp, 
  Map, 
  Users, 
  Calendar,
  Filter,
  Download,
  Grid,
  Maximize2,
  Minimize2,
  RefreshCw
} from 'lucide-react';
import { toast } from 'react-hot-toast';

import { CompetitivePositioningChart } from './CompetitivePositioningChart';
import { RevenueGrowthDashboard } from './RevenueGrowthDashboard';
import { TakeRateComparison } from './TakeRateComparison';
import { GeographicCoverageMap } from './GeographicCoverageMap';
import { CategoryBreakdownChart } from './CategoryBreakdownChart';
import { CompanyMetricsCard } from './CompanyMetricsCard';
import { MarketTrendsTimeline } from './MarketTrendsTimeline';

import { marketplaceCompanies } from '../data/company-profiles';
import { marketAnalysis } from '../data/market-analysis';
import type { FilterOptions } from '../types/visualization';

interface DashboardProps {
  className?: string;
}

type LayoutMode = 'grid' | 'focus';
type ComponentKey = 'positioning' | 'revenue' | 'takeRate' | 'geographic' | 'category' | 'metrics' | 'trends';

interface DashboardLayout {
  component: ComponentKey;
  span: number;
  priority: number;
}

const defaultLayout: DashboardLayout[] = [
  { component: 'positioning', span: 2, priority: 1 },
  { component: 'revenue', span: 2, priority: 2 },
  { component: 'takeRate', span: 1, priority: 3 },
  { component: 'category', span: 1, priority: 4 },
  { component: 'geographic', span: 2, priority: 5 },
  { component: 'trends', span: 2, priority: 6 },
  { component: 'metrics', span: 4, priority: 7 },
];

export const Dashboard: React.FC<DashboardProps> = ({ className = '' }) => {
  const [globalFilters, setGlobalFilters] = useState<FilterOptions>({});
  const [layoutMode, setLayoutMode] = useState<LayoutMode>('grid');
  const [focusedComponent, setFocusedComponent] = useState<ComponentKey | null>(null);
  const [showGlobalFilters, setShowGlobalFilters] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  // Calculate comparison data for metrics cards
  const comparisonData = useMemo(() => {
    const revenues = marketplaceCompanies
      .filter(c => c.status !== 'defunct')
      .map(c => c.financial_metrics.revenue.amount);
    const valuations = marketplaceCompanies
      .filter(c => c.status !== 'defunct' && c.financial_metrics.valuation)
      .map(c => c.financial_metrics.valuation!.amount);
    const users = marketplaceCompanies
      .filter(c => c.status !== 'defunct' && c.user_metrics?.active_users)
      .map(c => c.user_metrics!.active_users!.count);

    return {
      avgRevenue: revenues.reduce((sum, r) => sum + r, 0) / revenues.length,
      avgValuation: valuations.reduce((sum, v) => sum + v, 0) / valuations.length,
      avgUsers: users.reduce((sum, u) => sum + u, 0) / users.length,
    };
  }, []);

  const handleFilterChange = useCallback((newFilters: FilterOptions) => {
    setGlobalFilters(newFilters);
    toast.success('Filters updated across all visualizations');
  }, []);

  const handleDrillDown = useCallback((companyId: string, metric: string) => {
    const company = marketplaceCompanies.find(c => c.id === companyId);
    if (company) {
      setGlobalFilters({ ...globalFilters, company: companyId });
      toast.success(`Focused on ${company.name} - ${metric}`);
    }
  }, [globalFilters]);

  const handleExportDashboard = useCallback(async () => {
    try {
      // This would implement a comprehensive dashboard export
      toast.success('Dashboard export functionality would be implemented here');
      console.log('Exporting dashboard with filters:', globalFilters);
    } catch (error) {
      toast.error('Export failed');
    }
  }, [globalFilters]);

  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    try {
      // Simulate data refresh
      await new Promise(resolve => setTimeout(resolve, 1000));
      toast.success('Dashboard data refreshed');
    } catch (error) {
      toast.error('Refresh failed');
    } finally {
      setRefreshing(false);
    }
  }, []);

  const toggleComponentFocus = useCallback((component: ComponentKey) => {
    if (layoutMode === 'focus' && focusedComponent === component) {
      setLayoutMode('grid');
      setFocusedComponent(null);
    } else {
      setLayoutMode('focus');
      setFocusedComponent(component);
    }
  }, [layoutMode, focusedComponent]);

  const getComponentTitle = (component: ComponentKey): string => {
    switch (component) {
      case 'positioning': return 'Competitive Positioning';
      case 'revenue': return 'Revenue Growth';
      case 'takeRate': return 'Take Rate Comparison';
      case 'geographic': return 'Geographic Coverage';
      case 'category': return 'Category Breakdown';
      case 'metrics': return 'Company Metrics';
      case 'trends': return 'Market Trends';
      default: return '';
    }
  };

  const renderComponent = (component: ComponentKey, isFullSize: boolean = false) => {
    const commonProps = {
      filters: globalFilters,
      onFilterChange: handleFilterChange,
      className: `${isFullSize ? 'h-screen' : 'h-96'}`,
    };

    switch (component) {
      case 'positioning':
        return <CompetitivePositioningChart {...commonProps} />;
      case 'revenue':
        return <RevenueGrowthDashboard {...commonProps} />;
      case 'takeRate':
        return <TakeRateComparison {...commonProps} />;
      case 'geographic':
        return <GeographicCoverageMap {...commonProps} />;
      case 'category':
        return <CategoryBreakdownChart {...commonProps} />;
      case 'trends':
        return <MarketTrendsTimeline {...commonProps} />;
      case 'metrics':
        return (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {marketplaceCompanies
              .filter(company => !globalFilters.company || company.id === globalFilters.company)
              .filter(company => company.status !== 'defunct')
              .map(company => (
                <CompanyMetricsCard
                  key={company.id}
                  company={company}
                  comparisonData={comparisonData}
                  onDrillDown={handleDrillDown}
                  className="h-fit"
                />
              ))}
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className={`min-h-screen bg-gray-50 ${className}`}>
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                Local Service Marketplace Analysis
              </h1>
              <p className="text-sm text-gray-600">
                Comprehensive visualization dashboard for market intelligence
              </p>
            </div>
            <div className="flex items-center gap-3">
              {/* Layout Mode Toggle */}
              <div className="flex items-center bg-gray-100 rounded-md p-1">
                <button
                  onClick={() => setLayoutMode('grid')}
                  className={`p-2 rounded ${
                    layoutMode === 'grid'
                      ? 'bg-white shadow-sm text-gray-900'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                  title="Grid Layout"
                >
                  <Grid className="h-4 w-4" />
                </button>
                <button
                  onClick={() => setLayoutMode('focus')}
                  className={`p-2 rounded ${
                    layoutMode === 'focus'
                      ? 'bg-white shadow-sm text-gray-900'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                  title="Focus Mode"
                >
                  <Maximize2 className="h-4 w-4" />
                </button>
              </div>

              {/* Global Filters */}
              <button
                onClick={() => setShowGlobalFilters(!showGlobalFilters)}
                className="flex items-center gap-2 px-3 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50"
              >
                <Filter className="h-4 w-4" />
                Filters
                {(globalFilters.company || globalFilters.year || globalFilters.metricType) && (
                  <span className="w-2 h-2 bg-blue-500 rounded-full" />
                )}
              </button>

              {/* Refresh */}
              <button
                onClick={handleRefresh}
                disabled={refreshing}
                className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md disabled:opacity-50"
                title="Refresh Data"
              >
                <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
              </button>

              {/* Export */}
              <button
                onClick={handleExportDashboard}
                className="flex items-center gap-2 px-3 py-2 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700"
              >
                <Download className="h-4 w-4" />
                Export
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Global Filters Panel */}
      {showGlobalFilters && (
        <div className="bg-blue-50 border-b border-blue-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-blue-900 mb-1">Company</label>
                <select
                  value={globalFilters.company || ''}
                  onChange={(e) => handleFilterChange({ ...globalFilters, company: e.target.value || undefined })}
                  className="w-full px-3 py-2 border border-blue-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                >
                  <option value="">All Companies</option>
                  <option value="thumbtack">Thumbtack</option>
                  <option value="taskrabbit">TaskRabbit</option>
                  <option value="angi">Angi</option>
                  <option value="handy">Handy</option>
                  <option value="porch_group">Porch Group</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-blue-900 mb-1">Year</label>
                <select
                  value={globalFilters.year || ''}
                  onChange={(e) => handleFilterChange({ ...globalFilters, year: e.target.value ? parseInt(e.target.value) : undefined })}
                  className="w-full px-3 py-2 border border-blue-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                >
                  <option value="">All Years</option>
                  <option value="2024">2024</option>
                  <option value="2023">2023</option>
                  <option value="2022">2022</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-blue-900 mb-1">Metric Type</label>
                <select
                  value={globalFilters.metricType || ''}
                  onChange={(e) => handleFilterChange({ ...globalFilters, metricType: e.target.value as any || undefined })}
                  className="w-full px-3 py-2 border border-blue-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                >
                  <option value="">All Metrics</option>
                  <option value="revenue">Revenue</option>
                  <option value="valuation">Valuation</option>
                  <option value="gmv">GMV</option>
                  <option value="users">Users</option>
                </select>
              </div>
              <div className="flex items-end">
                <button
                  onClick={() => handleFilterChange({})}
                  className="px-4 py-2 text-sm text-blue-700 hover:text-blue-900 font-medium"
                >
                  Clear All Filters
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {layoutMode === 'grid' ? (
          /* Grid Layout */
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            {defaultLayout.map(({ component, span }) => (
              <div
                key={component}
                className={`lg:col-span-${span} relative group`}
              >
                {/* Component Header */}
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold text-gray-900">
                    {getComponentTitle(component)}
                  </h2>
                  <button
                    onClick={() => toggleComponentFocus(component)}
                    className="opacity-0 group-hover:opacity-100 p-1 text-gray-500 hover:text-gray-700 transition-opacity"
                    title="Focus on this component"
                  >
                    <Maximize2 className="h-4 w-4" />
                  </button>
                </div>
                {renderComponent(component)}
              </div>
            ))}
          </div>
        ) : (
          /* Focus Layout */
          <div className="relative">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-900">
                {focusedComponent ? getComponentTitle(focusedComponent) : 'Select a component to focus'}
              </h2>
              <div className="flex items-center gap-2">
                {/* Component Selector */}
                <select
                  value={focusedComponent || ''}
                  onChange={(e) => setFocusedComponent(e.target.value as ComponentKey)}
                  className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select Component</option>
                  {defaultLayout.map(({ component }) => (
                    <option key={component} value={component}>
                      {getComponentTitle(component)}
                    </option>
                  ))}
                </select>
                <button
                  onClick={() => setLayoutMode('grid')}
                  className="p-2 text-gray-500 hover:text-gray-700"
                  title="Exit focus mode"
                >
                  <Minimize2 className="h-4 w-4" />
                </button>
              </div>
            </div>
            {focusedComponent && renderComponent(focusedComponent, true)}
          </div>
        )}

        {/* Summary Cards */}
        <div className="mt-12 grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-blue-100 rounded-lg">
                <BarChart3 className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <div className="text-2xl font-bold text-gray-900">
                  ${marketAnalysis.total_market_size.amount}B
                </div>
                <div className="text-sm text-gray-600">Total Market Size</div>
              </div>
            </div>
            <div className="text-sm text-green-600 flex items-center gap-1">
              <TrendingUp className="h-3 w-3" />
              {marketAnalysis.total_market_size.growth_rate}% CAGR
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-green-100 rounded-lg">
                <Users className="h-6 w-6 text-green-600" />
              </div>
              <div>
                <div className="text-2xl font-bold text-gray-900">
                  {marketplaceCompanies.filter(c => c.status !== 'defunct').length}
                </div>
                <div className="text-sm text-gray-600">Active Companies</div>
              </div>
            </div>
            <div className="text-sm text-gray-500">
              Analyzed platforms
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Map className="h-6 w-6 text-purple-600" />
              </div>
              <div>
                <div className="text-2xl font-bold text-gray-900">
                  {marketAnalysis.segment_analysis.length}
                </div>
                <div className="text-sm text-gray-600">Service Categories</div>
              </div>
            </div>
            <div className="text-sm text-gray-500">
              Market segments
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <Calendar className="h-6 w-6 text-yellow-600" />
              </div>
              <div>
                <div className="text-2xl font-bold text-gray-900">
                  {marketAnalysis.trends.length}
                </div>
                <div className="text-sm text-gray-600">Key Trends</div>
              </div>
            </div>
            <div className="text-sm text-gray-500">
              Market drivers
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};